import type { AiModelSettings } from './modelSettings';

export type AiFeatureId = 'deck-power-assessment';

export type AiFeatureMode = 'structured' | 'text';

/** Static per-call token profile, used for estimates until the nightly usage stats cover a setting. */
export interface AiEstimateProfile {
  /** System prompt, schema and wrapper tokens; the caller's variable input is added on top. */
  fixedInputTokens: number;
  /** Typical visible (non-reasoning) output tokens. */
  outputTokens: number;
  /** Multiplier on the shared per-effort reasoning-token table. */
  reasoningScale: number;
}

export interface AiFeatureMeta {
  id: AiFeatureId;
  label: string;
  description: string;
  mode: AiFeatureMode;
  defaults: AiModelSettings;
  estimate: AiEstimateProfile;
  /** Strict JSON schema name sent to the Responses API; null for text features. */
  schemaName: string | null;
  /** Column header for this feature's subject in the AI log, e.g. "Deck". */
  targetLabel: string;
  maxInputChars: number;
  supportsConversation: boolean;
  /** Bumped whenever the server-side prompt changes, so runs stay comparable in the log. */
  promptVersion: number;
}

export const AI_FEATURES: Record<AiFeatureId, AiFeatureMeta> = {
  'deck-power-assessment': {
    id: 'deck-power-assessment',
    label: 'Deck Power Assessment',
    description: 'Rates a deck on a 1-10 power scale and, for Commander-family formats, assigns an official bracket.',
    mode: 'structured',
    defaults: { model: 'gpt-5.6-terra', effort: 'medium', verbosity: 'low', maxOutputTokens: 8_000 },
    estimate: { fixedInputTokens: 1_100, outputTokens: 420, reasoningScale: 1 },
    schemaName: 'deck_power_assessment',
    targetLabel: 'Deck',
    maxInputChars: 120_000,
    supportsConversation: false,
    promptVersion: 3,
  },
};

export const AI_FEATURE_IDS = Object.keys(AI_FEATURES) as AiFeatureId[];

export function isAiFeatureId(value: unknown): value is AiFeatureId {
  return typeof value === 'string' && value in AI_FEATURES;
}

/**
 * Rough token estimate used for pre-send UI hints and input caps. Real usage comes back on the run.
 */
export function estimateTokens(text: string): number {
  return Math.ceil(text.length / 4);
}
